class CustomProductInfo extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.productUrl = this.dataset.productUrl;
    this.sectionId = this.dataset.sectionId;
    this.lastOption1 = null;
    this.allVariants = JSON.parse(this.dataset.variants || "[]");
    // Bound references for cleanup
    this._variantHandlers = new Map();
    this._qtyDecreaseHandlers = new Map();
    this._qtyIncreaseHandlers = new Map();
    this._formHandler = null;
    this.currentUrl = new URL(window.location.href);
    this.isDefaultProductUrl = !this.currentUrl.searchParams.has("variant");
  }

  connectedCallback() {
    this.initVariantListeners();
    this.initQuantityButtons();
    this.initAddToCart();
    this.updateLastOption1();
    this.initProductGallerySliderWithRetry();
  }

  disconnectedCallback() {
    this._removeVariantListeners();
    this._removeQuantityListeners();
    this._removeFormListener();
  }

  // ─── Variant Listeners ───────────────────────────────────────────────────────

  _removeVariantListeners() {
    this._variantHandlers.forEach((handler, btn) => {
      btn.removeEventListener("click", handler);
    });
    this._variantHandlers.clear();
  }

  initVariantListeners() {
    this._removeVariantListeners();
    this.querySelectorAll(".--variant").forEach((btn) => {
      const handler = (e) => this.handleVariantClick(e);
      this._variantHandlers.set(btn, handler);
      btn.addEventListener("click", handler);
      btn.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          btn.click();
        }
      });
    });
  }

  handleVariantClick(e) {
    const clicked = e.currentTarget;
    if (clicked.getAttribute("aria-disabled") === "true") {
      return;
    }

    if (clicked.classList.contains("active")) {
      console.log("Option Already Selected");
      return;
    }

    const optionWrapper = clicked.closest(".option-wrapper");

    if (optionWrapper) {
      optionWrapper.querySelectorAll(".--variant").forEach((btn) => {
        btn.classList.remove("active");
        btn.setAttribute("aria-pressed", "false");
      });
    }
    clicked.classList.add("active");
    clicked.setAttribute("aria-pressed", "true");

    const selectedOptions = this.querySelectorAll(".--variant.active");

    this.updateVariant(selectedOptions);
  }

  async updateVariant(selectedOptions) {
    const start = performance.now();
    // const optionValues = Array.from(selectedOptions).map(
    //   (opt) => opt.dataset.valueid,
    // );

    // const allVariants = JSON.parse(this.dataset.variants);

    const optionValuesText = Array.from(selectedOptions).map(
      (opt) => opt.dataset.value,
    );

    const selectedVariant = this.allVariants.find((variant) =>
      variant.options.every(
        (option, index) => option === optionValuesText[index],
      ),
    );

    const variantId = selectedVariant?.id ?? null;

    if (!variantId) {
      console.error("No matching variant found");
      return;
    }

    const url = new URL(this.productUrl, window.location.origin);

    url.searchParams.delete("variant");
    url.searchParams.delete("option_values");

    url.searchParams.set("variant", variantId);
    url.searchParams.set("section_id", this.sectionId);

    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    const signal = this._abort.signal;

    this.classList.add("is-loading");
    console.log(
    "Time BEFORE fetch:",
    performance.now() - start,
    "ms"
  );

  const fetchStart = performance.now();

    try {
      const res = await fetch(url.toString(), { signal });

      console.log(
      "Fetch response:",
      performance.now() - fetchStart,
      "ms"
    );

      if (!res.ok) {
        throw new Error(`Variant fetch failed: ${res.status}`);
      }
      const textStart = performance.now();
      const html = await res.text();
      
       console.log(
      "res.text():",
      performance.now() - textStart,
      "ms"
    );

      const doc = new DOMParser().parseFromString(html, "text/html");

      const newCustomElement = doc.querySelector("custom-product-info");

      if (newCustomElement) {
        this.renderProductInfo(newCustomElement);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Variant update error:", err);
    } finally {
      if (!signal.aborted) this.classList.remove("is-loading");
    }
   
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  renderProductInfo(newEl) {
    // 1. Update URL
    const newVariantInput = newEl.querySelector('input[name="id"]');

    const shouldUpdateUrl =
      this.dataset.updateUrl !== "false" &&
      this.closest(".--main-product-section")?.dataset.updateUrl !== "false";

    if (newVariantInput && shouldUpdateUrl) {
      const variantId = newVariantInput.value;

      const browserUrl = new URL(window.location.href);

      browserUrl.searchParams.delete("section_id");
      browserUrl.searchParams.delete("option_values");
      browserUrl.searchParams.set("variant", variantId);

      window.history.replaceState(
        {},
        "",
        browserUrl.pathname + browserUrl.search + browserUrl.hash,
      );
    }

    // 2. Update hidden variant ID input
    const currentVariantInput = this.querySelector('input[name="id"]');
    if (currentVariantInput && newVariantInput) {
      currentVariantInput.value = newVariantInput.value;
    }

    // 3. Update price
    // const newPrice = newEl.querySelector(
    //   ".--price-container .--price-installments",
    // );
    // const currentPrice = this.querySelector(
    //   ".--price-container .--price-installments",
    // );
    const newPrice = newEl.querySelector(
      ".--price-container",
    );
    const currentPrice = this.querySelector(
      ".--price-container",
    );
    if (newPrice && currentPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }
    // Update Add to cart
    const newAtc = newEl.querySelector(".add-to-cart-wrapper");
    const currentAtc = this.querySelector(".add-to-cart-wrapper");
    if (newAtc && currentAtc) {
      currentAtc.innerHTML = newAtc.innerHTML;
    }
    //Inventory Update
    const newInventory = newEl.querySelector(".product-inventory");
    const currentInventory = this.querySelector(".product-inventory");

    if (newInventory && currentInventory) {
      currentInventory.innerHTML = newInventory.innerHTML;
    }
    // Update SKU
    const newSku = newEl.querySelector(".product-sku");
    const currentSku = this.querySelector(".product-sku");

    if (newSku && currentSku) {
      currentSku.innerHTML = newSku.innerHTML;
    }

    // === Update Pouch Size Legend from fetched section ===
    const newLegend = newEl.querySelector(".--range-slider .--selected-value");
    const oldLegend = this.querySelector(".--range-slider .--selected-value");

    if (newLegend && oldLegend) {
      oldLegend.innerHTML = newLegend.innerHTML;
    }
    // === Update --range-option-band from fetched section ===
    const newRangeBand = newEl.querySelector(
      ".--range-slider .--range-option-band",
    );
    const olRangeBand = this.querySelector(
      ".--range-slider .--range-option-band",
    );

    if (newRangeBand && olRangeBand) {
      olRangeBand.innerHTML = newRangeBand.innerHTML;
    }

    // 4. Update variant picker active states per option
    // Uses data-option-index on .variant-options (matches the Liquid template)
    newEl
      .querySelectorAll(".variant-options[data-option-index]")
      .forEach((newOptions) => {
        const index = newOptions.dataset.optionIndex;
        const currentOptions = this.querySelector(
          `.variant-options[data-option-index="${index}"]`,
        );
        if (currentOptions) {
          currentOptions.innerHTML = newOptions.innerHTML;
        }
      });

    // Keep the Local/National toggle in sync with the active service-area pill
    window.syncServiceAreaRadios(this);

    // 5. Re-init listeners after DOM update
    this.initVariantListeners();
    this.initQuantityButtons();
    this.initAddToCart();

    // 6. Gallery update only when Option 1 changes
    const activeOption1 = this.querySelector(
      ".option-wrapper:nth-child(2) .--variant.active",
    );
    const currentOption1Value = activeOption1
      ? activeOption1.dataset.value
      : null;

    //const currentUrl = new URL(window.location.href);
    // console.log("Updated v1 : ", this.isDefaultProductUrl, this.currentUrl);

    if (
      currentOption1Value !== this.lastOption1 ||
      this.isDefaultProductUrl === true
    ) {
      this.isDefaultProductUrl = false;
      
      // Update Gallery
      const newGallery = newEl.querySelector(".--product-media");
      const oldGallery = this.querySelector(".--product-media");

      if (newGallery && oldGallery) {
        // oldGallery.innerHTML = newGallery.innerHTML;
      }
      // this.initProductGallerySlider();

      this.lastOption1 = currentOption1Value;
    }
  }

  updateLastOption1() {
    const option1El = this.querySelector(
      ".option-wrapper:nth-child(2) .--variant.active",
    );
    if (option1El) {
      this.lastOption1 = option1El.dataset.value;
    }
  }

  // ─── Quantity ────────────────────────────────────────────────────────────────

  _removeQuantityListeners() {
    this._qtyDecreaseHandlers.forEach((handler, btn) =>
      btn.removeEventListener("click", handler),
    );
    this._qtyDecreaseHandlers.clear();

    this._qtyIncreaseHandlers.forEach((handler, btn) =>
      btn.removeEventListener("click", handler),
    );
    this._qtyIncreaseHandlers.clear();
  }

  initQuantityButtons() {
    this._removeQuantityListeners();
    const qtyInput = this.querySelector('input[name="quantity"]');
    if (!qtyInput) return;

    this.querySelectorAll(".--quantity-decrease").forEach((btn) => {
      const handler = () => {
        const val = parseInt(qtyInput.value) || 1;
        if (val > 1) qtyInput.value = val - 1;
      };
      this._qtyDecreaseHandlers.set(btn, handler);
      btn.addEventListener("click", handler);
    });

    this.querySelectorAll(".--quantity-increase").forEach((btn) => {
      const handler = () => {
        const val = parseInt(qtyInput.value) || 1;
        qtyInput.value = val + 1;
      };
      this._qtyIncreaseHandlers.set(btn, handler);
      btn.addEventListener("click", handler);
    });
  }

  _removeFormListener() {
    const form = this.querySelector("form.product-form");
    if (form && this._formHandler) {
      form.removeEventListener("submit", this._formHandler);
      this._formHandler = null;
    }
  }

  initAddToCart() {
    this._removeFormListener();
    const form = this.querySelector("form.product-form");
    if (!form) return;

    const addButton = form.querySelector("button.--add-to-cart-button");

    this._formHandler = async (e) => {
      e.preventDefault(); // Prevent default form submission

      // Check if button is disabled
      if (addButton && addButton.disabled) {
        console.log("Product variant is unavailable - cannot add to cart");
        return;
      }

      const qtyInput = this.querySelector('input[name="quantity"]');
      const variantInput = this.querySelector('input[name="id"]');

      if (!variantInput) {
        console.error("Missing  variant input");
        return;
      }

      const qty = parseInt(qtyInput?.value) || 1;
      const id = parseInt(variantInput.value);

      if (!id) {
        console.error("No valid variant ID");
        return;
      }

      const formData = {
        items: [
          {
            id: id,
            quantity: qty,
          },
        ],
        sections: "cart-drawer,cart-icon-bubble",
      };

      // Handle upsell products (if any exist in the section)
      const upsellProducts = document.querySelectorAll(
        `.section-container--${this.sectionId} .--complementary_product-container input:checked`,
      );
      // section-container--
      console.log("upsellProducts", upsellProducts);
      upsellProducts?.forEach((upsellProduct) => {
        formData.items.push({
          id: Number(upsellProduct.dataset.id),
          quantity: 1,
        });
      });

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          const cartDrawer = document.querySelector("cart-drawer");
          const data = await response.json();
          console.log("product added", data);
          let cartStatus = document.getElementById("cart-status");
          if (cartStatus) {
            cartStatus.textContent = "Product added to cart";
          }

          cartDrawer.renderContents(data);
          // cartDrawer.classList.remove('is-empty');

          window.location.href = "/checkout";
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error("Error adding to cart:", errorData);
        }
      } catch (error) {
        console.error("Network error while adding to cart:", error);
      }
    };

    form.addEventListener("submit", this._formHandler);
  }

  // ─── Gallery ─────────────────────────────────────────────────────────────────

  initProductGallerySliderWithRetry(attempt = 0) {
    const maxAttempts = 8;
    console.log("Attempt ", attempt);
    if (attempt >= maxAttempts) return;

    const gallery = this.closest(".--main-product-section")?.querySelector(
      ".--product-gallery",
    );

    if (typeof Swiper !== "undefined" && gallery) {
      this.initProductGallerySlider();
    } else {
      setTimeout(() => {
        this.initProductGallerySliderWithRetry(attempt + 1);
      }, 300);
    }
  }

  initProductGallerySlider() {
    if (typeof Swiper === "undefined") return;

    // Scoped to the section, not document-wide
    const section = this.closest(".--main-product-section");
    if (!section) return;

    const galleries = section.querySelectorAll(".--product-gallery");

    galleries.forEach((gallery) => {
      const mainEl = gallery.querySelector(".--main-slider");
      const thumbEl = gallery.querySelector(".--thumb-slider");
      const nextEl = gallery.querySelector(".swiper-button-next");
      const prevEl = gallery.querySelector(".swiper-button-prev");

      if (!mainEl || !thumbEl) return;

      if (mainEl.swiper) mainEl.swiper.destroy(true, true);
      if (thumbEl.swiper) thumbEl.swiper.destroy(true, true);

      const thumbSlider = new Swiper(thumbEl, {
        slidesPerView: 7,
        spaceBetween: 10,
        freeMode: true,

        watchSlidesProgress: true,
        direction: "horizontal",
        breakpoints: {
          // Desktop: 750px and above
          768: {
            direction: "vertical",
            slidesPerView: 5.5,
          },
        },
      });

      new Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 10,
        navigation: {
          nextEl: nextEl,
          prevEl: prevEl,
        },
        thumbs: {
          swiper: thumbSlider,
        },
        on: {
          slideChangeTransitionStart(swiper) {
            const videos = swiper.el.querySelectorAll(
              ".swiper-slide:not(.swiper-slide-active) video",
            );

            if (!videos.length) return;

            videos.forEach((video) => {
              if (!video.paused) {
                video.pause();
              }
            });
          },
        },
      });
    });
  }
}

if (!customElements.get("custom-product-info")) {
  customElements.define("custom-product-info", CustomProductInfo);
}

window.syncRangeToRadio = function () {
  const rangeSlider = document.getElementById("--steps");
  if (!rangeSlider) return;

  const selectedValue = parseInt(rangeSlider.value, 10);

  const variant = document.querySelector(
    `.--variant[data-range-index="${selectedValue}"]`,
  );

  if (variant && !variant.classList.contains("active")) {
    variant.click();
  }
};

window.handleServiceAreaChange = function (selectedRadio) {
  const optionObject = document.querySelector(
    ".option-wrapper.--option-name-service-area",
  );
  if (!optionObject) return;
  const want = selectedRadio.value.toLowerCase();
  const target = Array.from(optionObject.querySelectorAll(".--variant")).find(
    (btn) => (btn.dataset.value || "").toLowerCase().includes(want),
  );
  if (target) target.click();
};

window.syncServiceAreaRadios = function (root) {
  const active = (root || document).querySelector(
    ".option-wrapper.--option-name-service-area .--variant.active",
  );
  if (!active) return;
  const isLocal = (active.dataset.value || "").toLowerCase().includes("local");
  const scope = root || document;
  const local = scope.querySelector("#service-area-local");
  const national = scope.querySelector("#service-area-national");
  if (local) local.checked = isLocal;
  if (national) national.checked = !isLocal;
};

window.addEventListener("load", () => {
  if (!window.location.hash) return;

  const id = window.location.hash.slice(1);
  const target = document.getElementById(id);

  if (!target) return;

  setTimeout(() => {
    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, 300);
});
